import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  Heading,
  Input,
  Text,
  useToast,
  VStack,
  ScaleFade,
  Tooltip,
  Badge,
  HStack,
  Avatar,
  IconButton,
  Divider,
  Spinner,
  Textarea,
  Kbd,
  AvatarBadge,
  SlideFade,
  Icon,
} from '@chakra-ui/react';
import { 
  FaRegPaperPlane, 
  FaMicrophone, 
  FaStopCircle, 
  FaRegTrashAlt, 
  FaCopy, 
  FaRobot, 
  FaUserAstronaut, 
  FaRegSmile, 
  FaRegLightbulb, 
  FaRegStar
} from 'react-icons/fa';
import useAuth from '../store/useAuth';
import useCredits from '../store/useCredits';
import { COLORS, CREDIT_COSTS } from '../utils/constants';

// Typing animation - using CSS animation string
const typingAnimation = "typing 1s infinite";

// Add this to global styles or as inline style
const typingKeyframes = {
  "0%": { opacity: 0.3 },
  "50%": { opacity: 1 },
  "100%": { opacity: 0.3 }
};

// Loading dots animation
const LoadingDots = () => (
  <span className="loading-dots">
    <span style={{animation: "typing 1s infinite 0.1s"}}></span>
    <span style={{animation: "typing 1s infinite 0.2s"}}></span>
    <span style={{animation: "typing 1s infinite 0.3s"}}></span>
  </span>
);

// Persona options
const AI_PERSONAS = {
  name: "Ignita",
  tagline: "Creative AI Assistant",
  welcomeMessages: [
    "Hello! I'm Ignita, your creative AI companion. How can I inspire you today?",
    "Welcome! I'm here to help bring your creative ideas to life. What would you like to explore?",
    "Hi there! I'm your personal AI assistant. Let's create something amazing together!",
    "Greetings! I'm ready to assist with your creative projects. What's on your mind?"
  ]
};

function AIChat() {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [isThinking, setIsThinking] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [sessionId, setSessionId] = useState(null);
  
  const endOfMessagesRef = useRef(null);
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);
  
  const navigate = useNavigate();
  const toast = useToast();
  
  const { user, isLoggedIn } = useAuth();
  const { balance, deductCredits } = useCredits();
  
  // Enhanced AI suggestions with more personality
  const aiSuggestions = [
    "Create a sci-fi movie plot for me",
    "Help me write a catchy slogan for my business",
    "What's the best way to create fantasy character art?",
    "Can you suggest some creative prompt ideas?",
    "How do I make my AI-generated images more unique?"
  ];
  
  // Redirect to login if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
    }
  }, [isLoggedIn, navigate]);

  // Fetch credits when user is available
  useEffect(() => {
    if (user && user.walletAddress) {
      const { fetchBalance } = useCredits.getState();
      fetchBalance(user.walletAddress);
    }
  }, [user]);
  
  // Auto-scroll to bottom when new messages are added (only for AI responses)
  useEffect(() => {
    if (chatContainerRef.current && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      // Only auto-scroll for AI responses or when thinking
      if (lastMessage.sender === 'ai' || isThinking) {
        // Scroll the chat container to bottom
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    }
  }, [messages, isThinking]);
  
  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  
  // Animation on mount - with randomized welcome message
  useEffect(() => {
    setIsVisible(true);
    
    // Add a welcome message with random choice
    const randomWelcome = AI_PERSONAS.welcomeMessages[
      Math.floor(Math.random() * AI_PERSONAS.welcomeMessages.length)
    ];
    
    setMessages([
      {
        id: 1,
        sender: 'ai',
        text: randomWelcome,
        timestamp: new Date()
      }
    ]);
  }, []);
  
  const simulateTyping = (text) => {
    // [chat] Safety check for undefined/null text
    if (!text || typeof text !== 'string') {
      setIsTyping(false);
      return;
    }
    
    setIsTyping(true);
    setDisplayText("");
    setCurrentCharIndex(0);
    
    const interval = setInterval(() => {
      setCurrentCharIndex(prev => {
        if (prev >= text.length) {
          clearInterval(interval);
          setIsTyping(false);
          return prev;
        }
        setDisplayText(text.substring(0, prev + 1));
        return prev + 1;
      });
    }, 50);
  };
  
  // Calculate chat cost based on message length
  const getChatCost = (messageText) => {
    if (!messageText || messageText.trim() === '') return 0;
    // Base cost of 1 credit per message
    return 1;
  };
  
  const handleSendMessage = async () => {
    if (input.trim() === '') return;
    
    // Check if user has enough credits (AI chat costs 1 credit)
    if (balance < 1) {
      toast({
        title: 'Insufficient Credits',
        description: 'You need 1 credit to send a message. Please purchase more credits.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    
    const userMessage = {
      id: messages.length + 1,
      sender: 'user',
      text: input,
      timestamp: new Date(),
      isUser: true
    };
    
    setMessages(prevMessages => [...prevMessages, userMessage]);
    const currentInput = input;
    setInput('');
    setIsThinking(true);
    
    try {
      // Import the chat service
      const { sendChatMessage } = await import('../services/AIService');
      
      // Send message to backend with current sessionId
      const response = await sendChatMessage(currentInput, user.walletAddress, sessionId);
      console.debug('[chat-response]', response);
      
      // Store sessionId for subsequent messages in this conversation
      if (response.sessionId && !sessionId) {
        setSessionId(response.sessionId);
      }
      
      const aiResponse = {
        id: messages.length + 2,
        sender: 'ai',
        text: response.reply || response.response || 'No response received',
        timestamp: new Date(),
        isUser: false,
        model: response.model
      };
      
      setMessages(prevMessages => [...prevMessages, aiResponse]);
      
      // Simulate typing effect for better UX
      simulateTyping(response.reply || response.response || 'No response received');
      
    } catch (error) {
      console.error('Chat error:', error);
      
      let errorMessage = 'Failed to get AI response. Please try again.';
      
      if (error.message.includes('Insufficient credits')) {
        errorMessage = 'Insufficient credits for AI chat.';
      } else if (error.message.includes('temporarily unavailable')) {
        errorMessage = 'AI service is temporarily unavailable. Please try again later.';
      } else if (error.message.includes('Network error')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.message.includes('400')) {
        // Handle backend validation errors
        errorMessage = error.message;
      }
      
      toast({
        title: 'Chat Error',
        description: errorMessage,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsThinking(false);
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  const handleClearChat = () => {
    // Get a new random welcome message for reset
    const randomWelcome = AI_PERSONAS.welcomeMessages[
      Math.floor(Math.random() * AI_PERSONAS.welcomeMessages.length)
    ];
    
    // Reset with welcome message
    setMessages([{
      id: 1,
      sender: 'ai',
      text: randomWelcome,
      timestamp: new Date()
    }]);
    
    // Reset typing effect
    setIsTyping(false);
    setDisplayText("");
  };
  
  const handleToggleRecording = () => {
    // This would use the Web Speech API in a real implementation
    setIsRecording(!isRecording);
    
    if (!isRecording) {
      toast({
        title: 'Voice Recording Started',
        description: 'Speak now...',
        status: 'info',
        duration: null,
        isClosable: false,
        id: 'recording-toast'
      });
    } else {
      toast.close('recording-toast');
      // Simulate processing
      setTimeout(() => {
        setInput(prev => prev + " Voice transcription would appear here.");
      }, 500);
    }
  };
  
  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  return (
    <Container maxW="4xl" py={8}>
      <ScaleFade in={isVisible} initialScale={0.95}>
        <VStack spacing={0} align="stretch" height="85vh">
          {/* Chat header */}
          {/* <Flex 
            justify="space-between" 
            align="center" 
            mb={4}
            p={4}
            borderRadius="xl"
            bg="rgba(10, 24, 10, 0.6)"
            borderWidth="1px"
            borderColor="rgba(30, 220, 11, 0.2)"
            boxShadow="0 4px 6px rgba(0, 0, 0, 0.1)"
          >
            <HStack spacing={3}>
              <Box
                position="relative"
                borderRadius="full" 
                p={1}
                bg="rgba(30, 220, 11, 0.1)"
              >
                <Avatar 
                  size="md" 
                  bg={COLORS.primary}
                  color="black"
                  name={AI_PERSONAS.name} 
                  icon={<FaRobot size="1.5rem" />}
                >
                  <AvatarBadge boxSize='1.25em' bg='green.500' borderColor='black'>
                    <Box animation={`pulse 2s infinite`} />
                  </AvatarBadge>
                </Avatar>
              </Box>
              <Box>
                <Heading 
                  as="h1" 
                  size="md"
                  color="white"
                  letterSpacing="tight"
                  fontWeight="bold"
                  lineHeight="1.2"
                >
                  {AI_PERSONAS.name}
                </Heading>
                <Text fontSize="xs" color={COLORS.textSecondary} mt={0}>
                  {AI_PERSONAS.tagline}
                </Text>
              </Box>
            </HStack>
            
            <HStack>
              <Tooltip label="Chat uses credits per message">
                <Badge 
                  px={4} 
                  py={2} 
                  borderRadius="md" 
                  fontWeight="bold"
                  bg="rgba(30, 220, 11, 0.15)"
                  border="1px solid"
                  borderColor={COLORS.primary}
                  color={COLORS.accent}
                  className="pulse"
                >
                  Credits: {balance}
                </Badge>
              </Tooltip>
              
              <Tooltip label="Start a new conversation">
                <IconButton
                  aria-label="Clear chat"
                  icon={<FaRegTrashAlt />}
                  variant="ghost"
                  color={COLORS.textSecondary}
                  _hover={{ bg: "rgba(30, 220, 11, 0.1)", color: COLORS.primary }}
                  onClick={handleClearChat}
                />
              </Tooltip>
            </HStack>
          </Flex> */}
            {/* Chat container */}
          <Box 
            flex="1" 
            borderRadius="lg"
            border="2px dashed"
            borderColor="rgb(7, 74, 0)"
            background="linear-gradient(135deg, rgba(0, 20, 5, 0.8) 0%, rgba(0, 40, 10, 0.6) 100%)"
            boxShadow="0 8px 32px rgba(0, 0, 0, 0.3)"
            backdropFilter="blur(10px)"
            overflow="hidden"
            display="flex"
            flexDirection="column"
            position="relative"
            transition="all 0.3s ease"
            _hover={{
              borderColor: "rgba(30, 220, 11, 0.6)",
              background: "linear-gradient(135deg, rgba(0, 30, 8, 0.9) 0%, rgba(0, 50, 15, 0.7) 100%)",
              transform: "translateY(-2px)",
              boxShadow: "0 12px 48px rgba(30, 220, 11, 0.2)"
            }}
          >
            {/* Background pattern for chat */}
            <Box
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="0"
              opacity="0.03"
              pointerEvents="none"
              bgImage="url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgdmlld0JveD0iMCAwIDYwIDYwIj48cGF0aCBkPSJNMzAgNUMyOC42MTYgNSAyNy4zOTkgNi4xMTkgMjcuMzk5IDcuNUM3LjUwMSA3LjUgNyA4LjAwNSA3IDI3LjVDNS42MjcgMjcuNSA0LjUgMjguNjk3IDQuNSAzMEM0LjUgMzEuMzAzIDUuNjI3IDMyLjUgNyAzMi41QzcgNTIuMDAxIDcuMDU5IDUyLjUgMjcuMzk5IDUyLjVDMjcuMzk5IDUzLjg4MSAyOC42MTYgNTUgMzAgNTVDMzEuMzg0IDU1IDMyLjUgNTMuODgxIDMyLjUgNTIuNUM1Mi45NDEgNTIuNSA1MyA1Mi4wMDEgNTMgMzIuNUM1NC4zNzMgMzIuNSA1NS41IDMxLjMwMyA1NS41IDMwQzU1LjUgMjguNjk3IDU0LjM3MyAyNy41IDUzIDI3LjVDNTMgOC4wMDUgNTIuOTQxIDcuNSAzMi41IDcuNUMzMi41IDYuMTE5IDMxLjM4NCA1IDMwIDVNMzAgMTBDNTAgMTAgNTAgMTAgNTAgMzBDNTAgNTAgNTAgNTAgMzAgNTBDMTAgNTAgMTAgNTAgMTAgMzBDMTAgMTAgMTAgMTAgMzAgMTAiIGZpbGw9IiMxRURDMEIiIGZpbGwtcnVsZT0iZXZlbm9kZCIvPjwvc3ZnPg==')"
              bgSize="120px"
              zIndex="0"
            />
            
            {/* Messages area */}
            <Box 
              ref={chatContainerRef}
              flex="1" 
              overflowY="auto" 
              p={6}
              position="relative"
              zIndex="1"
              css={{
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'rgba(0, 0, 0, 0.1)',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: COLORS.primary,
                  borderRadius: '8px',
                },
              }}
            >
              <VStack spacing={5} align="stretch">
                {messages.map((message, index) => (
                  <SlideFade 
                    key={message.id} 
                    in={true} 
                    offsetY="20px"
                    delay={0.1}
                  >
                    <Flex 
                      justify={message.sender === 'user' ? 'flex-end' : 'flex-start'}
                      mb={2}
                    >
                      {/* AI Avatar for AI messages */}
                      {/* {message.sender === 'ai' && (
                        <Avatar 
                          size="sm" 
                          bg={COLORS.primary}
                          color="black"
                          name={AI_PERSONAS.name}
                          icon={<FaRobot />}
                          mr={2}
                          mt={1}
                        />
                      )} */}
                      
                      <Box
                        maxW="85%"
                        bg={message.sender === 'user' 
                          ? 'rgba(30, 220, 11, 0.15)' 
                          : 'black'}
                        borderRadius={"15px"}
                        borderWidth="1px"
                        border={"none"}
                        padding={"11px 15px 27px"}
                        position="relative"
                        boxShadow={message.sender === 'user'
                          ? '0 10px 6px rgba(0, 0, 0, 0.1)'
                          : '0 4px 6px rgba(0, 0, 0, 0.1)'}
         
                        transition="all 0.2s"
                      >
                        {/* Message header with sender & time */}
                        {/* <Flex 
                          justify="space-between" 
                          align="center" 
                          mb={2}
                        >
                          <Text 
                            fontSize="xs" 
                            color={message.sender === 'user' 
                              ? 'rgba(255, 255, 255, 0.7)' 
                              : COLORS.primary}
                            fontWeight="medium"
                          >
                            {message.sender === 'user' ? 'You' : AI_PERSONAS.name}
                          </Text>
                          
                          <Text fontSize="xs" color="gray.500">
                            {formatTime(message.timestamp)}
                          </Text>
                        </Flex> */}
                        
                        {/* Message content */}
                        <Text 
                          color={COLORS.text} 
                          whiteSpace="pre-wrap" 
                          fontSize="md"
                          lineHeight="1.6"
                          textAlign={message.sender === 'user' ? 'left' : 'left'}
                        >
                          {/* For latest AI message, show typing effect */}
                          {message.sender === 'ai' && 
                           index === messages.length - 1 && 
                           isTyping ? displayText : message.text}
                          
                          {/* Blinking cursor for typing effect */}
                          {message.sender === 'ai' && 
                           index === messages.length - 1 && 
                           isTyping && (
                            <Box 
                              as="span" 
                              borderRight="2px solid" 
                              borderColor={COLORS.primary} 
                              ml={1}
                              animation={typingAnimation}
                            />
                          )}
                        </Text>

                        {/* Action buttons for AI messages */}
                        {message.sender === 'ai' && (
                          <HStack 
                            spacing={1} 
                            position="absolute" 
                            bottom={1} 
                            right={1}
                            opacity={0.7}
                            _groupHover={{ opacity: 1 }}
                            transition="opacity 0.2s"
                          >
                            <Tooltip label="Copy to clipboard">
                              <IconButton
                                aria-label="Copy message"
                                icon={<FaCopy size="0.8rem" />}
                                size="xs"
                                variant="ghost"
                                color={COLORS.textSecondary}
                                opacity={0.6}
                                _hover={{ opacity: 1, color: COLORS.primary }}
                                onClick={() => {
                                  navigator.clipboard.writeText(message.text);
                                }}
                              />
                            </Tooltip>
                            
                            {/* <Tooltip label="Show alternatives">
                              <IconButton
                                aria-label="Show alternatives"
                                icon={<FaRegLightbulb size="0.8rem" />}
                                size="xs"
                                variant="ghost"
                                color={COLORS.textSecondary}
                                opacity={0.6}
                                _hover={{ opacity: 1, color: COLORS.primary }}
                              />
                            </Tooltip> */}
                          </HStack>
                        )}
                        
                        {/* User icon for user messages */}
                        {/* {message.sender === 'user' && (
                          <Box
                            position="absolute"
                            bottom={0}
                            right={-10}
                          >
                            <Avatar 
                              size="sm" 
                              bg="gray.700"
                              icon={<FaUserAstronaut />}
                              name={user?.name || 'User'}
                            />
                          </Box>
                        )} */}
                      </Box>
                    </Flex>
                  </SlideFade>
                ))}
                
                {/* Thinking indicator */}
                {isThinking && (
                  <SlideFade in={isThinking} offsetY="20px">
                    <Flex justify="flex-start">
                      {/* <Avatar 
                        size="sm" 
                        bg={COLORS.primary}
                        color="black"
                        name={AI_PERSONAS.name}
                        icon={<FaRobot />}
                        mr={2}
                        mt={1}
                      /> */}
                      
                      <Box 
                        bg="black"
                        borderRadius="15px"
                        borderWidth="1px"
                        borderColor="black"
                        p={4}
                        position="relative"
                      >
                        <HStack spacing={2}>
                          <Text color={COLORS.text}>Thinking</Text>
                          <LoadingDots />
                        </HStack>
                      </Box>
                    </Flex>
                  </SlideFade>
                )}
                
                <div ref={endOfMessagesRef} />
              </VStack>
            </Box>
            
            {/* <Divider borderColor="rgba(30, 220, 11, 0.3)" />
            
            
            {messages.length <= 2 && (
              <Box p={4} bg="rgba(0, 0, 0, 0.3)">
                <HStack mb={2}>
                  <Icon as={FaRegStar} color={COLORS.primary} />
                  <Text fontSize="sm" color={COLORS.primary} fontWeight="medium">
                    Try asking about:
                  </Text>
                </HStack>
                <Flex flexWrap="wrap" gap={2} px={4}>
                  {aiSuggestions.map((suggestion, index) => (
                    <Badge 
                      key={index}
                      px={3}
                      py={2}
                      borderRadius="full"
                      bg="rgba(30, 220, 11, 0.07)"
                      color={COLORS.text}
                      cursor="pointer"
                      fontSize="sm"
                      _hover={{ 
                        bg: "rgba(30, 220, 11, 0.2)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)"
                      }}
                      onClick={() => setInput(suggestion)}
                      transition="all 0.2s"
                    >
                      {suggestion}
                    </Badge>
                  ))}
                </Flex>
              </Box>
            )} */}
            
            {/* Input area with improved styling */}
            <Box p={5} bg="rgba(0, 0, 0, 0.5)" position="relative">
              <FormControl>
                <Flex>
                  <Box position="relative" flex="1">
                    <Textarea
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={handleKeyPress}
                      placeholder="Type your message..."
                      bg="rgba(0, 0, 0, 0.3)"
                      border="1px solid"
                      borderColor="rgba(30, 220, 11, 0.3)"
                      _hover={{ borderColor: COLORS.primary }}
                      _focus={{ 
                        borderColor: COLORS.primary,
                        boxShadow: `0 0 0 1px ${COLORS.primary}`
                      }}
                      color={COLORS.text}
                      resize="none"
                      rows={2}
                      mr={3}
                      borderRadius="xl"
                      p={3}
                      fontSize="md"
                      fontFamily="system-ui, sans-serif"
                      transition="all 0.2s"
                    />
                    
                    {/* Keyboard shortcut hint */}
                    {/* <HStack 
                      position="absolute" 
                      bottom={2} 
                      right={4}
                      spacing={1}
                      opacity={0.6}
                    >
                      <Kbd fontSize="xs" opacity={0.7} color="gray.400">Enter</Kbd>
                      <Text fontSize="xs" color="gray.400">to send</Text>
                    </HStack> */}
                  </Box>
                  
                  <VStack spacing={2} paddingLeft={"10px"}>
                    {/* <Tooltip label={isRecording ? "Stop recording" : "Voice input"}>
                      <IconButton
                        aria-label="Voice input"
                        icon={isRecording ? <FaStopCircle /> : <FaMicrophone />}
                        colorScheme={isRecording ? "red" : "green"}
                        variant={isRecording ? "solid" : "outline"}
                        onClick={handleToggleRecording}
                        size="md"
                        isRound
                        boxShadow="0 2px 4px rgba(0, 0, 0, 0.2)"
                      />
                    </Tooltip> */}
                    
                    <Tooltip label="Send message">
                      <IconButton
                        aria-label="Send message"
                        icon={<FaRegPaperPlane />}
                        bg={COLORS.primary}
                        color="black"
                        _hover={{ bg: 'green.400' }}
                        onClick={handleSendMessage}
                        isDisabled={input.trim() === '' || isThinking}
                        size="md"
                        isRound
                        boxShadow="0 2px 4px rgba(0, 0, 0, 0.2)"
                      />
                    </Tooltip>
                  </VStack>
                </Flex>
                
                <Flex justify="space-between" mt={1} px={2}>
                  {/* <Text fontSize="xs" color={COLORS.textSecondary}>
                    {input.length > 0 ? 
                      `Cost: ${getChatCost(input)} credits` : 
                      'Messages cost credits based on length'}
                  </Text> */}
                  
                  <Text fontSize="xs" color={COLORS.textSecondary}>
                    {input.length > 0 ? `${input.length}/4000` : ''}
                  </Text>
                </Flex>
              </FormControl>
            </Box>
          </Box>
        </VStack>
      </ScaleFade>
    </Container>
  );
}

export default AIChat; 